'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, Suspense } from 'react';
import { PostHogProvider as PHProvider, usePostHog } from 'posthog-js/react';
import { initPostHog, posthog } from '@/lib/posthog';

/**
 * PostHog Provider for React
 *
 * Story 6.6: A/B Variant Refinement & Admin Analytics
 *
 * Features:
 * - Initializes PostHog client on mount
 * - Tracks pageviews on route changes (Next.js App Router compatible)
 * - Provides PostHog hooks via context
 */

/**
 * Inner component that handles pageview tracking
 * Must be inside PHProvider to use usePostHog hook
 */
function PostHogPageviewTracker(): null {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthogInstance = usePostHog();

  useEffect(() => {
    if (pathname && posthogInstance) {
      let url = window.origin + pathname;
      const params = searchParams?.toString();
      if (params) {
        url = url + `?${params}`;
      }

      // Capture pageview with full URL
      posthogInstance.capture('$pageview', {
        $current_url: url,
      });
    }
  }, [pathname, searchParams, posthogInstance]);

  return null;
}

/**
 * Wrapped pageview tracker with Suspense boundary
 * useSearchParams requires Suspense in Next.js App Router
 */
function PageviewTracker(): React.JSX.Element {
  return (
    <Suspense fallback={null}>
      <PostHogPageviewTracker />
    </Suspense>
  );
}

interface PostHogProviderProps {
  children: React.ReactNode;
}

/**
 * PostHog Provider Component
 *
 * Wrap your app with this provider to enable PostHog analytics.
 *
 * @example
 * ```tsx
 * // In layout.tsx
 * import { PostHogProvider } from '@/providers/PostHogProvider';
 *
 * export default function Layout({ children }) {
 *   return (
 *     <PostHogProvider>
 *       {children}
 *     </PostHogProvider>
 *   );
 * }
 * ```
 */
export function PostHogProvider({
  children,
}: PostHogProviderProps): React.JSX.Element {
  useEffect(() => {
    // Initialize PostHog on client side
    initPostHog();
  }, []);

  // If PostHog key is not set, just render children without provider
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) {
    return <>{children}</>;
  }

  return (
    <PHProvider client={posthog}>
      <PageviewTracker />
      {children}
    </PHProvider>
  );
}
